import React, { useState, useEffect, useRef, useMemo } from 'react';
import './PreviousOrders.css';
import { useTranslation } from 'react-i18next';
import image1 from '../../../assets/Image1.png';
import Slider from 'react-slick';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa6';
import { Skeleton } from '@mui/material';
import useWindowSize from './useWindowSize';
import Loader from '../../../Components/Loader/Loader';
import axiosUserInstance from '../../../axiosUserInstance';
import axiosVendorInstance from '../../../axiosVendorInstance';
import { useCart } from '../../../CartProvider';
import { IoPlayOutline } from 'react-icons/io5';

function PrevOrders() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [previousOrders, setPreviousOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [currentSlide, setCurrentSlide] = useState(0);
    const windowSize = useWindowSize();
    const [loading, setLoading] = useState(false);
    const { fetchCartCount } = useCart();
    const videoRefs = useRef({});
    const [playingIndex, setPlayingIndex] = useState(null);
    
    const handlePlay = (e, index) => {
        e.stopPropagation();
        
        Object.entries(videoRefs.current).forEach(([key, video]) => {
            if (parseInt(key) !== index && video && !video.paused) {
                video.pause();
            }
        });
        
        const currentVideo = videoRefs.current[index];
        if (currentVideo) {
            currentVideo.play();
            setPlayingIndex(index);
        }
    };

    useEffect(() => {
        const fetchPreviousOrders = async () => {
            try {
                const response = await axiosUserInstance.get(
                    '/orders/previous-orders-with-vendor?pageNo=1&pageSize=10'
                );
                setPreviousOrders(response?.data?.orders);
            } catch (error) {
                console.error('Error fetching previous orders', error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchPreviousOrders();
    }, []);

    const handlePreviousOrderPage = (orderId) => {
        navigate(`/orders/${orderId}`);
    };

    const handleOrderAgain = async (e, orderId) => {
        e.stopPropagation();

        try {
            setLoading(true);
            const response = await axiosVendorInstance.get(
                `/order/reorder/ordersdata?order_id=${orderId}`, {skipAuth: true}
            );
            if(response?.status == 200){
                setTimeout(() => {
                    setLoading(false);
                }, 2000);
                navigate('/cart');
                await fetchCartCount();
            }
        } catch (error) {
            setLoading(false);
            console.error('Error fetching previous orders again', error);
        } finally {
            setIsLoading(false);
        }
    };

    const NextArrow = ({ onClick, isDisabled }) => (
        <div
            className={`custom-arrow custom-next ${isDisabled ? 'disabled-arrow' : ''}`}
            onClick={!isDisabled ? onClick : undefined}
        >
            <FaArrowRight />
        </div>
    );
        
    const PrevArrow = ({ onClick, isDisabled }) => (
        <div
            className={`custom-arrow custom-prev ${isDisabled ? 'disabled-arrow' : ''}`}
            onClick={!isDisabled ? onClick : undefined}
        >
            <FaArrowLeft />
        </div>
    );

    let slidesToShow = 8;
    let showSlider = previousOrders?.length > 8;
    const totalSlides = previousOrders?.length || 0;
    let variableWidth = false;

    // Apply responsive settings in correct order (smallest to largest)
    if (windowSize.width <= 330) {
        slidesToShow = 3.5;
        showSlider = previousOrders?.length >= 4;
    } else if (windowSize.width <= 370) {
        slidesToShow = 4;
        showSlider = previousOrders?.length >= 4;
    } else if (windowSize.width <= 411) {
        slidesToShow = 4.5;
        showSlider = previousOrders?.length >= 5;
    }else if (windowSize.width <= 443) {
        slidesToShow = 5;
        showSlider = previousOrders?.length >= 5;
    } else if (windowSize.width <= 488) {
        slidesToShow = 5.5;
        showSlider = previousOrders?.length >= 6;
    } else if (windowSize.width <= 524) {
        slidesToShow = 6;
        showSlider = previousOrders?.length >= 6;
    } else if (windowSize.width <= 555) {
        slidesToShow = 6.5;
        showSlider = previousOrders?.length >= 6;
    } else if (windowSize.width <= 600) {
        slidesToShow = 7;
        showSlider = previousOrders?.length >= 7;
    } else if (windowSize.width <= 768) {
        slidesToShow = 3;
        showSlider = previousOrders?.length >= 3;
    } else if (windowSize.width <= 1165) {
        slidesToShow = 4;
        showSlider = previousOrders?.length >= 4;
    } else if (windowSize.width <= 1363) {
        slidesToShow = 5;
        showSlider = previousOrders?.length >= 5;
    } else if (windowSize.width <= 1677) {
        slidesToShow = 7;
        showSlider = previousOrders?.length >= 7;
    } else if (windowSize.width <= 2560) {
        slidesToShow = 8;
        showSlider = previousOrders?.length >= 8;
    }

    const settings = useMemo(() => ({
        dots: false,
        infinite: false,
        centerMode: false,
        speed: 500,
        slidesToShow,
        slidesToScroll: 1,
        swipeToSlide: true,
        touchMove: true,
        initialSlide: 0,
        nextArrow: !isLoading && windowSize.width > 600 ? <NextArrow isDisabled={currentSlide >= totalSlides - slidesToShow} /> : <></>,
        prevArrow: !isLoading && windowSize.width > 600 ? <PrevArrow isDisabled={currentSlide === 0} /> : <></>,
        swipe: true,
        variableWidth,
        afterChange: (index) => setCurrentSlide(index),
    }), [isLoading, windowSize.width, currentSlide, totalSlides, slidesToShow, variableWidth]);


    return (
        previousOrders &&
    previousOrders.length > 0 && (
            <div className='Prev-orders-Main-container'>
                {loading && (
                    <div>
                        <Loader/>
                    </div>
                )}
                <div className='title-and-arrows'>
                    <h5 className='previous-orders-title-homepage'>
                        {t('homescreen.previous-orders-title')}
                    </h5>
                </div>

                <div className='order-cards-container'>
                    {isLoading ? (
                        <Slider {...settings}>
                            {[...Array(10)].map((_, index) => (
                                <div key={index} className='order-card'>
                                    <Skeleton variant='rounded' width={windowSize.width > 600 ? 100 : 71}
                                        height={windowSize.width > 600 ? 100 : 71} />
                                    <Skeleton variant='text' width={windowSize.width > 600 ? 80 : 60} height={20} />
                                    <Skeleton variant='rounded' width={windowSize.width > 600 ? 100 : 71} height={25} />
                                </div>
                            ))}
                        </Slider>
                    ) : showSlider ? (
                        <Slider {...settings} className='slider-component'>
                            {previousOrders?.map((item, index) => (
                                <div
                                    key={index}
                                    className='order-card'
                                    onClick={() =>handlePreviousOrderPage(item?.OrderInfo?.OrderID)}
                                >
                                    <div className="food-media-previous-order-wrapper">
                                        {item?.OrderInfo?.OrderInfo?.[0]?.MenuItems[0]
                                            ?.MenuItemAvatarKey?.match(/\.(mp4|webm|ogg)$/i) ? (
                                                <div className="previous-order-video-container1">
                                                    <video
                                                        src={item?.OrderInfo?.OrderInfo?.[0]?.MenuItems[0]
                                                            ?.MenuItemAvatarKey}
                                                        muted
                                                        playsInline
                                                        // eslint-disable-next-line react/no-unknown-property
                                                        webkit-playsinline="true"
                                                        preload="metadata"
                                                        className="previous-order-food-video"
                                                        ref={(el) => videoRefs.current[index] = el}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const video = videoRefs.current[index];
                                                            if (video && !video.paused) {
                                                                video.pause();
                                                                setPlayingIndex(null);
                                                            }
                                                        }}
                                                        onEnded={() => setPlayingIndex(null)}
                                                    />
                                                    {playingIndex !== index && (
                                                        <button onClick={(e) => handlePlay(e, index)} className='play-btn'>
                                                            <IoPlayOutline color='white' fontSize={34} />
                                                        </button>
                                                    )}

                                                </div>
                                            ) : (
                                                <img
                                                    className='Food-previous-order-image'
                                                    src={item?.OrderInfo?.OrderInfo?.[0]?.MenuItems[0]
                                                        ?.MenuItemAvatarKey || image1}
                                                    alt='Food img'
                                                    onError={(e) => {
                                                        e.target.src = image1;
                                                    }}
                                                />
                                            )}
                                    </div>
                                    <h6 className='items-name'>
                                        {item?.OrderInfo?.OrderInfo?.[0]?.MenuItems[0]?.MenuItemName
                                            ?.length > 13
                                            ? item?.OrderInfo?.OrderInfo?.[0]?.MenuItems[0]?.MenuItemName?.slice(0, 13) + '...'
                                            : item?.OrderInfo?.OrderInfo?.[0]?.MenuItems[0]
                                                ?.MenuItemName || 'pav bhaji'}
                                    </h6>
                                    <div className='button-text-container'>
                                        <button className='Order-Button' onClick={(e) => handleOrderAgain(e,item?.OrderInfo?.OrderID)}>
                                            <p className='Button-text'>
                                                {t('homescreen.order-again-button')}
                                            </p>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </Slider>
                    ) : (
                        <div className='flex-orders-container'>
                            {previousOrders?.map((item, index) => (
                                <div
                                    key={index}
                                    className='order-card'
                                    onClick={() =>handlePreviousOrderPage(item?.OrderInfo?.OrderID)}
                                >
                                    <div className="food-media-previous-order-wrapper">
                                        {item?.OrderInfo?.OrderInfo?.[0]?.MenuItems[0]
                                            ?.MenuItemAvatarKey?.match(/\.(mp4|webm|ogg)$/i) ? (
                                                <div className="previous-order-video-container1">
                                                    <video
                                                        src={item?.OrderInfo?.OrderInfo?.[0]?.MenuItems[0]
                                                            ?.MenuItemAvatarKey}
                                                        muted
                                                        playsInline
                                                        // eslint-disable-next-line react/no-unknown-property
                                                        webkit-playsinline="true"
                                                        preload="metadata"
                                                        className="previous-order-food-video"
                                                        ref={(el) => videoRefs.current[index] = el}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            const video = videoRefs.current[index];
                                                            if (video && !video.paused) {
                                                                video.pause();
                                                                setPlayingIndex(null);
                                                            }
                                                        }}
                                                        onEnded={() => setPlayingIndex(null)}
                                                    />
                                                    {playingIndex !== index && (
                                                        <button onClick={(e) => handlePlay(e, index)} className='play-btn'>
                                                            <IoPlayOutline color='white' fontSize={34} />
                                                        </button>
                                                    )}

                                                </div>
                                            ) : (
                                                <img
                                                    className='Food-previous-order-image'
                                                    src={item?.OrderInfo?.OrderInfo?.[0]?.MenuItems[0]
                                                        ?.MenuItemAvatarKey || image1}
                                                    alt='Food img'
                                                    onError={(e) => {
                                                        e.target.src = image1;
                                                    }}
                                                />
                                            )}
                                    </div>
                                    <h6 className='items-name'>
                                        {item?.OrderInfo?.OrderInfo?.[0]?.MenuItems[0]?.MenuItemName
                                            ?.length > 12
                                            ? item?.OrderInfo?.OrderInfo?.[0]?.MenuItems[0]?.MenuItemName?.slice(0, 12) + '...'
                                            : item?.OrderInfo?.OrderInfo?.[0]?.MenuItems[0]
                                                ?.MenuItemName || 'pav bhaji'}
                                    </h6>
                                    <div className='button-text-container'>
                                        <button className='Order-Button' onClick={(e) => handleOrderAgain(e,item?.OrderInfo?.OrderID)}>
                                            <p className='Button-text'>
                                                {t('homescreen.order-again-button')}
                                            </p>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        )
    );
}

export default PrevOrders;
